import type { ColumnDef } from "@tanstack/react-table";
import { Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { submissionTypeLabel } from "@/features/submissions/lib/submission-display";
import { formatOptionalArabicDateTime } from "@/lib/formatters";
import type { Submission } from "@/types/submission";

interface SubmissionsTableProps {
  submissions: Submission[];
  pageIndex: number;
  pageCount: number;
  onPageChange: (pageIndex: number) => void;
  onViewDetail: (submission: Submission) => void;
}

export function SubmissionsTable({
  submissions,
  pageIndex,
  pageCount,
  onPageChange,
  onViewDetail,
}: SubmissionsTableProps) {
  const columns: ColumnDef<Submission>[] = [
    {
      id: "type",
      header: "النوع",
      accessorFn: (row) => submissionTypeLabel(row.type),
    },
    {
      id: "name",
      header: "الاسم",
      accessorFn: (row) => row.name ?? "—",
    },
    {
      id: "email",
      header: "البريد الإلكتروني",
      accessorFn: (row) => row.email ?? "—",
    },
    {
      id: "subject",
      header: "الموضوع",
      accessorFn: (row) => row.subject ?? "—",
    },
    {
      id: "department",
      header: "الدائرة",
      accessorFn: (row) =>
        row.department ? (row.department.nameAr ?? row.department.name) : "—",
    },
    {
      // CR-001 FR-1.8 — attachment indicator. «المرفق» is the column label the spec fixes (§2.2).
      id: "attachment",
      header: "المرفق",
      cell: ({ row }) =>
        row.original.hasAttachment ? (
          <span
            className="inline-flex items-center gap-1 text-sm text-foreground"
            title={row.original.attachment?.originalName}
          >
            <Paperclip className="size-4" aria-hidden="true" />
            <span className="sr-only">يوجد مرفق</span>
          </span>
        ) : (
          <span className="text-muted-foreground" aria-label="لا يوجد مرفق">
            —
          </span>
        ),
    },
    {
      id: "createdAt",
      header: "تاريخ الإرسال",
      accessorFn: (row) => formatOptionalArabicDateTime(row.createdAt),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewDetail(row.original)}
        >
          عرض
        </Button>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={submissions}
      manualPagination={{ pageIndex, pageCount, onPageChange }}
    />
  );
}
